/**
 * Camera — computes the `ProjectionCenter` (viewport transform: cover zoom +
 * screen-space offset) for a given player position, canvas size, and map size.
 *
 * PURE. No Pixi, no DOM, no time. Imports only from `./projection.js` and
 * `./hudLayout.js` — both equally pure (the render-scope lint rule keeps this
 * module dependency-thin, and `render/` never imports from `ui/`).
 *
 * Behavior (WP10, reworked by #730, retargeted at the arena viewport by #781):
 *   • "follow": center the local player in the ARENA VIEWPORT (the canvas minus
 *     the reserved bottom HUD strip — `hudLayout.ts`), clamped so the projected
 *     map rectangle NEVER leaves an arena-viewport edge; the player drifts
 *     off-center near an edge instead. Combined with #730's axis-aligned basis
 *     and the bounded zoom below, that clamp is exactly the "zero black"
 *     guarantee as #781 restates it: the map rect is at least
 *     arena-viewport-sized and always covers the arena viewport, from every
 *     camera position, corners included. Its ZOOM is `viewportFollowZoom`
 *     (#782): `max(zero-black floor, the zoom that shows exactly
 *     `TARGET_WORLD_H` world units of height)`, so the visible vertical world
 *     extent is the same at every resolution and never the whole map — a bigger
 *     screen buys bigger pixels, not more information.
 *   • "arenaCenter": static PURE FIT over the FULL canvas — identical to
 *     `computeArenaCenter`, still on the cover-zoom floor (#782 leaves this mode
 *     alone). Used by the demo harness / visual baseline where a moving camera
 *     would make whole-scene screenshots flake; it has no live action bar, so it
 *     reserves nothing.
 *
 * The camera adds NO new world↔screen conversion — it only produces the
 * transform that `worldToScreen(x, y, center)` (and the translated + scaled
 * Pixi containers) consume. Following uses the interpolated `RenderWorldPos`
 * so the camera moves in lockstep with the drawn player (no easing).
 */

import { arenaViewportHeight } from "./hudLayout.js";
import {
  projectedMapSize,
  viewportFollowZoom,
  worldToScreen,
  zoomOnly,
  type ProjectionCenter,
  type RenderWorldPos,
} from "./projection.js";

/** Camera behavior mode. "follow" = WP10 player-follow with edge clamp;
 *  "arenaCenter" = legacy static fit (demo harness / visual baseline). */
export type CameraMode = "follow" | "arenaCenter";

/** Clamp `v` into `[lo, hi]`. Presentation-layer float; no assertions. */
function clamp(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

/**
 * Compute the camera transform for a player-follow view with arena-edge
 * clamping. The zoom is the BOUNDED follow zoom (`viewportFollowZoom`, #782)
 * over `canvasW × arenaH`: `max(zero-black floor, target-vertical-extent
 * zoom)`, so the projected map rectangle is at least arena-viewport-sized on
 * both axes (usually strictly bigger — the target extent normally binds); per
 * axis the camera then tracks the player, clamped so the map rect never leaves
 * an arena-viewport edge. The clamp is UNCHANGED by #782: raising zoom above
 * the cover floor only makes the map cover the arena viewport more easily. The
 * "map still smaller than the viewport" branch survives only for degenerate
 * inputs (zero-size canvas or map, where no zoom can help) — there it
 * statically centers.
 *
 * `playerPos === null` (no snapshot yet, or no entity for the local
 * seat) → static centering in the same arena viewport AT THE SAME ZOOM, so the
 * framing does not jump when the first snapshot lands. (That is why this is not
 * `computeArenaCenter`: since #782 that function is the demo harness / visual
 * baseline's PURE-FIT mode and deliberately stays on the cover floor.) A dead
 * player's corpse still carries a `pos`, so the caller keeps framing it — no
 * special case here.
 *
 * `hudReserveBottomPx` (#679, reworked by #730, made a real layout region by
 * #781) is the height of the bottom HUD strip: it is subtracted from the canvas
 * height ONCE, up front, and the resulting `arenaH` then drives BOTH the cover
 * zoom and the `cy` clamp. It is no longer a centering-only bias, which is what
 * let a champion at the map's south edge clamp behind the action bar. Y-axis
 * only — the strip is bottom-anchored and never obscures a horizontal (X) edge.
 * Defaults to 0 so a caller with no HUD overlay gets the full canvas
 * (`"arenaCenter"` mode, the demo harness / visual baseline, keeps that).
 * See `hudLayout.ts` for the reserve formula and its floor.
 */
export function computeCameraCenter(
  playerPos: RenderWorldPos | null,
  canvasW: number,
  canvasH: number,
  mapW: number,
  mapH: number,
  hudReserveBottomPx = 0,
): ProjectionCenter {
  // The arena viewport is `canvasW × arenaH`. Everything below is expressed
  // over it — #781's invariant is "every pixel of the ARENA VIEWPORT shows
  // playable area"; the reserved strip below it is interface, not arena.
  // `arenaViewportHeight` is the defensive boundary (see its doc): `arenaH > 0`
  // for any positive canvas, whatever reserve a caller hands us.
  const arenaH = arenaViewportHeight(canvasH, hudReserveBottomPx);

  const zoom = viewportFollowZoom(canvasW, arenaH, mapW, mapH);
  // Projected map bounding box at this zoom, before translation: the map
  // rectangle spans [0, mapPx.w] × [0, mapPx.h] from the projected origin
  // (#730 — an axis-aligned basis has no negative extent and no diamond bbox).
  const mapPx = projectedMapSize(mapW, mapH, zoom);

  if (playerPos === null) {
    return { cx: (canvasW - mapPx.w) / 2, cy: (arenaH - mapPx.h) / 2, zoom };
  }

  // Player's projected point at zero translation — through the ONE forward
  // conversion, never a re-derived copy of its math.
  const p = worldToScreen(playerPos.wx, playerPos.wy, zoomOnly(zoom));

  // Desired transform puts the player at the midpoint of the arena viewport.
  const cxDesired = canvasW / 2 - p.sx;
  const cyDesired = arenaH / 2 - p.sy;

  // Per-axis: static-center when the map cannot cover the arena viewport
  // (degenerate inputs only); else follow-and-clamp so the map covers every
  // arena-viewport pixel.
  const cx = mapPx.w <= canvasW ? (canvasW - mapPx.w) / 2 : clamp(cxDesired, canvasW - mapPx.w, 0);
  const cy = mapPx.h <= arenaH ? (arenaH - mapPx.h) / 2 : clamp(cyDesired, arenaH - mapPx.h, 0);

  return { cx, cy, zoom };
}
